import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { activityDaysApi, eventsApi, casesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
    ArrowLeft,
    Calendar,
    Plus,
    Clock,
    Users,
    Trash2,
    Upload,
    Image,
    Video,
    AlertCircle,
    Save,
    X,
    Camera,
    Send,
    Edit,
    Check,
    Moon
} from 'lucide-react';
import { formatDate, formatMinutesToHours } from '../lib/utils';
import EventMediaItem from '../components/EventMediaItem';
import AgentHoursManager from '../components/AgentHoursManager';
import DragDropUpload from '../components/DragDropUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

export default function ActivityDayDetail() {
    const { caseId, dayId } = useParams();
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const [dayData, setDayData] = useState(null);
    const [caseData, setCaseData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    
    // Form states
    const [serviceStart, setServiceStart] = useState('');
    const [serviceEnd, setServiceEnd] = useState('');
    const [transferTime, setTransferTime] = useState('');
    const [operatorsCount, setOperatorsCount] = useState('1');
    const [vehiclesCount, setVehiclesCount] = useState('1');
    const [vehicleType, setVehicleType] = useState('auto');
    const [notes, setNotes] = useState('');
    
    // Quick event form - sempre visibile
    const [eventTime, setEventTime] = useState('');
    const [eventDescription, setEventDescription] = useState('');
    const [eventNextDay, setEventNextDay] = useState(false);
    const [addingEvent, setAddingEvent] = useState(false);
    
    // Edit event state
    const [editingEventId, setEditingEventId] = useState(null);
    const [editEventTime, setEditEventTime] = useState('');
    const [editEventDescription, setEditEventDescription] = useState('');
    const [editEventNextDay, setEditEventNextDay] = useState(false);
    const [savingEvent, setSavingEvent] = useState(false);
    
    // Media states
    const [uploadingEventId, setUploadingEventId] = useState(null);

    useEffect(() => {
        loadData();
    }, [caseId, dayId]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [dayRes, caseRes] = await Promise.all([
                activityDaysApi.get(caseId, dayId),
                casesApi.get(caseId)
            ]);
            setDayData(dayRes.data);
            setCaseData(caseRes.data);
            
            setServiceStart(dayRes.data.service_start_time || '');
            setServiceEnd(dayRes.data.service_end_time || '');
            setTransferTime(dayRes.data.transfer_time_minutes?.toString() || '');
            setOperatorsCount(dayRes.data.operators_count?.toString() || '1');
            setVehiclesCount(dayRes.data.vehicles_count?.toString() || '1');
            setVehicleType(dayRes.data.vehicle_type || 'auto');
            setNotes(dayRes.data.notes || '');
        } catch (err) {
            setError('Errore nel caricamento');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveDetails = async () => {
        try {
            setSaving(true);
            const response = await activityDaysApi.update(caseId, dayId, {
                service_start_time: serviceStart || null,
                service_end_time: serviceEnd || null,
                transfer_time_minutes: transferTime ? parseInt(transferTime) : null,
                operators_count: parseInt(operatorsCount) || 1,
                vehicles_count: parseInt(vehiclesCount) || 1,
                vehicle_type: vehicleType,
                notes: notes
            });
            setDayData(response.data);
        } catch (err) {
            setError('Errore nel salvataggio');
        } finally {
            setSaving(false);
        }
    };

    const handleQuickAddEvent = async (e) => {
        e.preventDefault();
        if (!eventTime || !eventDescription.trim()) return;
        
        try {
            setAddingEvent(true);
            const response = await eventsApi.add(caseId, dayId, {
                time: eventTime,
                description: eventDescription.trim(),
                next_day: eventNextDay
            });
            setDayData({
                ...dayData,
                events: [...(dayData.events || []), response.data]
            });
            // Reset e prepara per prossimo evento
            setEventDescription('');
            // Non resettare eventNextDay per comodità se si sta inserendo una serie di eventi notturni
            // Avanza l'ora di 15 minuti per comodità
            const [h, m] = eventTime.split(':').map(Number);
            const newMinutes = (m + 15) % 60;
            const newHours = m + 15 >= 60 ? (h + 1) % 24 : h;
            setEventTime(`${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`);
        } catch (err) {
            setError('Errore nell\'aggiunta');
        } finally {
            setAddingEvent(false);
        }
    };

    const handleDeleteEvent = async (eventId) => {
        if (!window.confirm('Eliminare questo evento?')) return;
        try {
            await eventsApi.delete(caseId, dayId, eventId);
            setDayData({
                ...dayData,
                events: dayData.events.filter(e => e.id !== eventId)
            });
        } catch (err) {
            setError('Errore eliminazione');
        }
    };

    const handleUploadMedia = async (eventId, files) => {
        if (!files?.length) return;
        
        // Salva la posizione scroll corrente
        const scrollPosition = window.scrollY;
        
        try {
            setUploadingEventId(eventId);
            setError('');
            for (const file of Array.from(files)) {
                await eventsApi.uploadMedia(caseId, dayId, eventId, file, '');
            }
            await loadData();
            
            // Ripristina la posizione scroll dopo un breve delay per permettere il re-render
            setTimeout(() => {
                window.scrollTo(0, scrollPosition);
            }, 150);
        } catch (err) {
            setError('Errore upload: ' + (err.response?.data?.detail || err.message));
        } finally {
            setUploadingEventId(null);
        }
    };

    const handleDeleteMedia = async (eventId, mediaId) => {
        // Salva la posizione scroll
        const scrollPosition = window.scrollY;
        
        try {
            await eventsApi.deleteMedia(caseId, dayId, eventId, mediaId);
            await loadData();
            
            // Ripristina scroll
            setTimeout(() => {
                window.scrollTo(0, scrollPosition);
            }, 150);
        } catch (err) {
            setError('Errore eliminazione');
        }
    };
    
    // Funzioni per modifica evento
    const startEditEvent = (event) => {
        setEditingEventId(event.id);
        setEditEventTime(event.time);
        setEditEventDescription(event.description);
        setEditEventNextDay(event.next_day || false);
    };
    
    const cancelEditEvent = () => {
        setEditingEventId(null);
        setEditEventTime('');
        setEditEventDescription('');
        setEditEventNextDay(false);
    };
    
    const saveEditEvent = async () => {
        if (!editEventTime.trim() || !editEventDescription.trim()) {
            setError('Orario e descrizione sono obbligatori');
            return;
        }
        
        const scrollPosition = window.scrollY;
        
        try {
            setSavingEvent(true);
            setError('');
            
            await eventsApi.update(caseId, dayId, editingEventId, {
                time: editEventTime,
                description: editEventDescription,
                next_day: editEventNextDay
            });
            
            await loadData();
            cancelEditEvent();
            
            setTimeout(() => {
                window.scrollTo(0, scrollPosition);
            }, 100);
        } catch (err) {
            setError('Errore nel salvataggio: ' + (err.response?.data?.detail || err.message));
        } finally {
            setSavingEvent(false);
        }
    };

    const handleDeleteDay = async () => {
        if (!window.confirm('Eliminare questa giornata?')) return;
        try {
            await activityDaysApi.delete(caseId, dayId);
            navigate(`/cases/${caseId}`);
        } catch (err) {
            setError('Errore eliminazione');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#00FF41]/30 border-t-[#00FF41] rounded-full animate-spin" />
            </div>
        );
    }

    if (!dayData) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <p className="text-white/60">Giornata non trovata</p>
                    <Button onClick={() => navigate(`/cases/${caseId}`)} className="mt-4 bg-[#00FF41] text-black rounded-none">
                        Torna al caso
                    </Button>
                </div>
            </div>
        );
    }

    // Ordina eventi: prima quelli senza next_day, poi quelli con next_day, entrambi per orario
    const sortedEvents = [...(dayData.events || [])].sort((a, b) => {
        // Prima ordina per next_day (false prima di true)
        if ((a.next_day || false) !== (b.next_day || false)) {
            return (a.next_day || false) ? 1 : -1;
        }
        // Poi ordina per orario
        return a.time.localeCompare(b.time);
    });

    return (
        <div className="min-h-screen bg-[#050505]">
            <div className="noise-overlay" />

            {/* Header compatto */}
            <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 py-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/cases/${caseId}`)} className="text-white/60 hover:text-white p-1">
                                <ArrowLeft className="w-5 h-5" />
                            </Button>
                            <Calendar className="w-5 h-5 text-[#00FF41]" />
                            <span className="font-mono text-[#00FF41] text-lg">{formatDate(dayData.date)}</span>
                            <span className="text-white/40 text-sm">{caseData?.case_number}</span>
                        </div>
                        <Button variant="outline" size="sm" onClick={handleDeleteDay} className="border-red-500/50 text-red-500 hover:bg-red-500/10 rounded-none">
                            <Trash2 className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </header>

            {error && (
                <div className="max-w-7xl mx-auto px-4 mt-4">
                    <div className="bg-red-500/10 border border-red-500 p-3 flex items-center justify-between">
                        <span className="text-red-500 text-sm">{error}</span>
                        <button onClick={() => setError('')}><X className="w-4 h-4 text-red-500" /></button>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto px-4 py-6">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    
                    {/* Colonna sinistra - Dettagli servizio compatti */}
                    <div className="lg:col-span-1">
                        <div className="bg-[#0A0A0A] border border-white/10 p-4 space-y-3">
                            <h3 className="font-heading text-sm font-bold text-white flex items-center gap-2">
                                <Clock className="w-4 h-4 text-[#00FF41]" />
                                Servizio
                            </h3>
                            
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label className="text-white/50 text-[10px] uppercase">Inizio</Label>
                                    <Input type="time" value={serviceStart} onChange={(e) => setServiceStart(e.target.value)}
                                        data-testid="service-start"
                                        className="bg-black border-white/20 text-white text-sm h-8 rounded-none font-mono" />
                                </div>
                                <div>
                                    <Label className="text-white/50 text-[10px] uppercase">Fine</Label>
                                    <Input type="time" value={serviceEnd} onChange={(e) => setServiceEnd(e.target.value)}
                                        data-testid="service-end"
                                        className="bg-black border-white/20 text-white text-sm h-8 rounded-none font-mono" />
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label className="text-white/50 text-[10px] uppercase">Trasf. (min)</Label>
                                    <Input type="number" value={transferTime} onChange={(e) => setTransferTime(e.target.value)}
                                        placeholder="0" className="bg-black border-white/20 text-white text-sm h-8 rounded-none font-mono" />
                                </div>
                                <div>
                                    <Label className="text-white/50 text-[10px] uppercase">Operatori</Label>
                                    <Input type="number" min="1" value={operatorsCount} onChange={(e) => setOperatorsCount(e.target.value)}
                                        className="bg-black border-white/20 text-white text-sm h-8 rounded-none font-mono" />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label className="text-white/50 text-[10px] uppercase">Automezzi</Label>
                                    <Input type="number" min="1" value={vehiclesCount} onChange={(e) => setVehiclesCount(e.target.value)}
                                        className="bg-black border-white/20 text-white text-sm h-8 rounded-none font-mono" />
                                </div>
                                <div>
                                    <Label className="text-white/50 text-[10px] uppercase">Tipo</Label>
                                    <select 
                                        value={vehicleType} 
                                        onChange={(e) => setVehicleType(e.target.value)}
                                        className="w-full bg-black border border-white/20 text-white text-sm h-8 rounded-none font-mono px-2"
                                    >
                                        <option value="auto">Auto</option>
                                        <option value="moto">Moto</option>
                                        <option value="altro">Altro</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <Label className="text-white/50 text-[10px] uppercase">Note</Label>
                                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                                    placeholder="Note..." className="bg-black border-white/20 text-white text-sm rounded-none" />
                            </div>

                            <Button onClick={handleSaveDetails} disabled={saving} className="w-full bg-[#00FF41] text-black text-sm h-8 rounded-none">
                                <Save className="w-3 h-3 mr-1" />
                                {saving ? '...' : 'SALVA'}
                            </Button>

                            {dayData.total_service_minutes > 0 && (
                                <div className="pt-3 border-t border-white/10 space-y-1">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-white/50">Durata</span>
                                        <span className="text-[#00FF41] font-mono">{formatMinutesToHours(dayData.total_service_minutes)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-white/50">Operatori</span>
                                        <span className="text-white font-mono">{dayData.operators_count}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-white/50">Automezzi</span>
                                        <span className="text-white font-mono">{dayData.vehicles_count} ({dayData.vehicle_type})</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Ore Agenti - solo quelli schedulati in questa data */}
                        {caseData?.assigned_agents?.length > 0 && (
                            <div className="mt-4">
                                <AgentHoursManager
                                    caseId={caseId}
                                    dayId={dayId}
                                    assignedAgents={caseData.assigned_agents}
                                    agentSchedules={caseData.agent_schedules || {}}
                                    dayDate={dayData.date}
                                    existingHours={dayData.agent_hours}
                                    serviceStart={dayData.service_start_time || serviceStart}
                                    serviceEnd={dayData.service_end_time || serviceEnd}
                                    transferTimeMinutes={dayData.transfer_time_minutes ?? parseInt(transferTime || '0', 10)}
                                    onUpdate={loadData}
                                />
                            </div>
                        )}
                    </div>

                    {/* Colonna destra - Timeline eventi */}
                    <div className="lg:col-span-3">
                        <div className="bg-[#0A0A0A] border border-white/10 p-4">
                            
                            {/* Form rapido sempre visibile */}
                            <form onSubmit={handleQuickAddEvent} className="mb-6 p-3 bg-[#00FF41]/5 border border-[#00FF41]/30">
                                {/* Prima riga: Ora, Toggle giorno dopo, Bottone */}
                                <div className="flex gap-2 items-end mb-3 sm:mb-0">
                                    <div className="w-20 sm:w-24 flex-shrink-0">
                                        <Label className="text-[#00FF41] text-[10px] uppercase font-bold">Ora</Label>
                                        <Input type="time" value={eventTime} onChange={(e) => setEventTime(e.target.value)} required
                                            data-testid="event-time"
                                            className="bg-black border-[#00FF41]/50 text-[#00FF41] h-9 rounded-none font-mono text-base sm:text-lg" />
                                    </div>
                                    {/* Descrizione - visibile solo su desktop nella prima riga */}
                                    <div className="hidden sm:block flex-1 min-w-0">
                                        <Label className="text-[#00FF41] text-[10px] uppercase font-bold">Descrizione evento</Label>
                                        <Input value={eventDescription} onChange={(e) => setEventDescription(e.target.value)} required
                                            placeholder="Cosa è successo..." 
                                            autoComplete="off"
                                            autoCorrect="off"
                                            autoCapitalize="off"
                                            spellCheck="false"
                                            data-testid="event-description"
                                            className="bg-black border-[#00FF41]/50 text-white h-9 rounded-none" />
                                    </div>
                                    {/* Spacer per spingere bottoni a destra su mobile */}
                                    <div className="flex-1 sm:hidden"></div>
                                    {/* Toggle giorno dopo */}
                                    <button
                                        type="button"
                                        onClick={() => setEventNextDay(!eventNextDay)}
                                        data-testid="event-next-day-toggle"
                                        className={`h-9 px-2 sm:px-3 flex items-center gap-1 border rounded-none transition-colors flex-shrink-0 ${
                                            eventNextDay 
                                                ? 'bg-blue-500/20 border-blue-500 text-blue-400' 
                                                : 'bg-black border-white/20 text-white/40 hover:border-white/40'
                                        }`}
                                        title="Evento del giorno dopo (dopo mezzanotte)"
                                    >
                                        <Moon className="w-4 h-4" />
                                        <span className="text-xs font-bold">+1</span>
                                    </button>
                                    <Button type="submit" disabled={addingEvent || !eventTime || !eventDescription.trim()}
                                        className="bg-[#00FF41] text-black h-9 px-3 sm:px-4 rounded-none font-bold flex-shrink-0">
                                        <Send className="w-4 h-4 sm:mr-1" />
                                        <span className="hidden sm:inline">{addingEvent ? '...' : 'AGGIUNGI'}</span>
                                    </Button>
                                </div>
                                {/* Seconda riga: Descrizione - visibile solo su mobile */}
                                <div className="sm:hidden mt-2">
                                    <Label className="text-[#00FF41] text-[10px] uppercase font-bold">Descrizione evento</Label>
                                    <Input value={eventDescription} onChange={(e) => setEventDescription(e.target.value)} required
                                        placeholder="Cosa è successo..." 
                                        autoComplete="off"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck="false"
                                        data-testid="event-description-mobile"
                                        className="bg-black border-[#00FF41]/50 text-white h-10 rounded-none text-base" />
                                </div>
                            </form>

                            {/* Lista eventi */}
                            {sortedEvents.length === 0 ? (
                                <div className="text-center py-12 text-white/30">
                                    <Clock className="w-10 h-10 mx-auto mb-3 opacity-30" />
                                    <p>Nessun evento - usa il form sopra per iniziare</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {sortedEvents.map((event) => (
                                        <div key={event.id} className={`bg-black/40 border-l-2 p-3 ${event.next_day ? 'border-blue-500' : 'border-[#00FF41]'}`}>
                                            {/* Header evento */}
                                            {editingEventId === event.id ? (
                                                /* Modalità modifica */
                                                <div className="space-y-3">
                                                    {/* Riga 1: ora + toggle giorno dopo (compatta) */}
                                                    <div className="flex gap-2 items-end">
                                                        <div className="w-24">
                                                            <Label className="text-[#00FF41] text-[10px] uppercase font-bold">Ora</Label>
                                                            <Input 
                                                                type="time" 
                                                                value={editEventTime} 
                                                                onChange={(e) => setEditEventTime(e.target.value)}
                                                                data-testid={`edit-event-time-${event.id}`}
                                                                className="bg-black border-[#00FF41]/50 text-[#00FF41] h-9 rounded-none font-mono text-lg" 
                                                            />
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => setEditEventNextDay(!editEventNextDay)}
                                                            data-testid={`edit-event-next-day-${event.id}`}
                                                            className={`h-9 px-3 flex items-center gap-1.5 border rounded-none transition-colors ${
                                                                editEventNextDay 
                                                                    ? 'bg-blue-500/20 border-blue-500 text-blue-400' 
                                                                    : 'bg-black border-white/20 text-white/40 hover:border-white/40'
                                                            }`}
                                                            title="Evento del giorno dopo (dopo mezzanotte)"
                                                        >
                                                            <Moon className="w-4 h-4" />
                                                            <span className="text-xs font-bold">+1</span>
                                                        </button>
                                                    </div>
                                                    {/* Riga 2: textarea a larghezza piena */}
                                                    <div>
                                                        <Label className="text-[#00FF41] text-[10px] uppercase font-bold">Descrizione</Label>
                                                        <Textarea 
                                                            value={editEventDescription} 
                                                            onChange={(e) => setEditEventDescription(e.target.value)}
                                                            rows={4}
                                                            autoComplete="off"
                                                            autoCorrect="off"
                                                            autoCapitalize="off"
                                                            spellCheck="false"
                                                            data-testid={`edit-event-description-${event.id}`}
                                                            className="bg-black border-[#00FF41]/50 text-white rounded-none resize-y min-h-[110px] w-full" 
                                                        />
                                                    </div>
                                                    <div className="flex justify-end gap-2">
                                                        <Button 
                                                            variant="ghost" 
                                                            size="sm" 
                                                            onClick={cancelEditEvent}
                                                            disabled={savingEvent}
                                                            data-testid={`cancel-edit-event-${event.id}`}
                                                            className="text-white/60 hover:text-white rounded-none"
                                                        >
                                                            <X className="w-4 h-4 mr-1" />
                                                            Annulla
                                                        </Button>
                                                        <Button 
                                                            size="sm" 
                                                            onClick={saveEditEvent}
                                                            disabled={savingEvent || !editEventTime.trim() || !editEventDescription.trim()}
                                                            data-testid={`save-edit-event-${event.id}`}
                                                            className="bg-[#00FF41] text-black rounded-none"
                                                        >
                                                            <Check className="w-4 h-4 mr-1" />
                                                            {savingEvent ? 'Salvataggio...' : 'Salva'}
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                /* Modalità visualizzazione */
                                                <div className="flex items-start gap-3">
                                                    <div className="flex items-center gap-1">
                                                        <span className={`font-mono text-xl font-bold whitespace-nowrap ${event.next_day ? 'text-blue-400' : 'text-[#00FF41]'}`}>
                                                            {event.time}
                                                        </span>
                                                        {event.next_day && (
                                                            <span className="text-blue-400 text-xs font-bold flex items-center gap-0.5" title="Giorno dopo">
                                                                <Moon className="w-3 h-3" />+1
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="flex-1 text-white text-sm pt-1">{event.description}</p>
                                                    <div className="flex items-center gap-1">
                                                        <Button 
                                                            variant="ghost" 
                                                            size="sm" 
                                                            onClick={() => startEditEvent(event)}
                                                            data-testid={`edit-event-btn-${event.id}`}
                                                            className="text-[#00FF41]/50 hover:text-[#00FF41] hover:bg-[#00FF41]/10 p-1 h-auto"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                        <Button 
                                                            variant="ghost" 
                                                            size="sm" 
                                                            onClick={() => handleDeleteEvent(event.id)}
                                                            data-testid={`delete-event-btn-${event.id}`}
                                                            className="text-red-500/50 hover:text-red-500 hover:bg-red-500/10 p-1 h-auto"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Media dell'evento con Drag & Drop */}
                                            <div className="mt-3 pt-3 border-t border-white/5">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Camera className="w-3 h-3 text-white/40" />
                                                    <span className="text-white/40 text-xs">
                                                        {event.media?.length || 0} file
                                                    </span>
                                                </div>
                                                
                                                {/* Drag & Drop Upload Area */}
                                                <DragDropUpload
                                                    onFilesSelected={(files) => handleUploadMedia(event.id, files)}
                                                    onError={(msg) => setError(msg)}
                                                    accept="image/*,video/*"
                                                    multiple={true}
                                                    disabled={uploadingEventId === event.id}
                                                    className="rounded-none mb-3"
                                                    data-testid={`event-media-dropzone-${event.id}`}
                                                >
                                                    <div className="p-4 text-center">
                                                        <Upload className={`w-6 h-6 mx-auto mb-2 ${uploadingEventId === event.id ? 'text-[#00FF41] animate-pulse' : 'text-white/40'}`} />
                                                        <p className="text-sm text-white/60">
                                                            {uploadingEventId === event.id 
                                                                ? 'Caricamento in corso...' 
                                                                : 'Trascina foto/video qui o clicca per selezionare'
                                                            }
                                                        </p>
                                                        <p className="text-xs text-white/40 mt-1">
                                                            Immagini e Video
                                                        </p>
                                                    </div>
                                                </DragDropUpload>

                                                {/* Grid media */}
                                                {event.media?.length > 0 && (
                                                    <div className="flex flex-wrap gap-2">
                                                        {event.media.map((media) => (
                                                            <EventMediaItem
                                                                key={media.id}
                                                                caseId={caseId}
                                                                dayId={dayId}
                                                                eventId={event.id}
                                                                media={media}
                                                                onDelete={(mediaId) => handleDeleteMedia(event.id, mediaId)}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
